import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Database, Search, FileText, User, Download } from 'lucide-react';

export default function DataBankPage() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {};
        if (search) params.search = search;
        if (filterBranch && filterBranch !== 'all') params.branch_id = filterBranch;
        if (filterStatus && filterStatus !== 'all') params.emp_status = filterStatus;
        const [eRes, bRes] = await Promise.all([
          api.get('/data-bank', { params }),
          api.get('/branches')
        ]);
        setEmployees(eRes.data);
        setBranches(bRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [search, filterBranch, filterStatus]);

  const handleExport = () => {
    const token = localStorage.getItem('hrms_token');
    const base = process.env.REACT_APP_BACKEND_URL;
    fetch(`${base}/api/export/employees`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'employee_data_bank.csv'; a.click(); });
  };

  const activeCount = employees.filter(e => e.emp_status !== 'exited').length;
  const exitedCount = employees.filter(e => e.emp_status === 'exited').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="data-bank-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Employee Data Bank</h1>
          <p className="text-neutral-500 text-sm mt-1">Complete employee records archive — including exited staff</p>
        </div>
        <Button variant="outline" className="rounded-sm" onClick={handleExport} data-testid="export-databank-button">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Total Records</p>
            <p className="text-2xl font-heading font-black text-neutral-950">{employees.length}</p>
          </CardContent>
        </Card>
        <Card className="border border-emerald-200 shadow-sm rounded-md bg-emerald-50/30">
          <CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Active</p>
            <p className="text-2xl font-heading font-black text-emerald-700">{activeCount}</p>
          </CardContent>
        </Card>
        <Card className="border border-red-200 shadow-sm rounded-md bg-red-50/30">
          <CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-red-600">Exited</p>
            <p className="text-2xl font-heading font-black text-red-700">{exitedCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border border-neutral-200 shadow-sm rounded-md">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <Input placeholder="Search by name, email, phone, Aadhar..." value={search}
                onChange={e => setSearch(e.target.value)} className="pl-9 h-10 rounded-sm" data-testid="databank-search" />
            </div>
            <Select value={filterBranch} onValueChange={setFilterBranch}>
              <SelectTrigger className="w-full sm:w-48 h-10 rounded-sm" data-testid="databank-branch-filter">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-36 h-10 rounded-sm" data-testid="databank-status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="exited">Exited</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Employee Table */}
      <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Employee</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden sm:table-cell">Contact</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Role</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden md:table-cell">Branch</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden md:table-cell">Joining</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Status</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden lg:table-cell">Docs</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1,2,3,4,5].map(i => <TableRow key={i}><TableCell colSpan={8}><div className="h-10 bg-neutral-100 rounded animate-pulse" /></TableCell></TableRow>)
              ) : employees.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-neutral-500">
                  <Database className="h-8 w-8 mx-auto mb-2 text-neutral-300" /> No records found
                </TableCell></TableRow>
              ) : employees.map(emp => (
                <TableRow key={emp.id} className={`hover:bg-neutral-50 ${emp.emp_status === 'exited' ? 'bg-neutral-50/50 opacity-75' : ''}`}
                  data-testid={`databank-row-${emp.id}`}>
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium text-neutral-900">{emp.name}</p>
                      <p className="text-[10px] text-neutral-400">{emp.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-neutral-600 hidden sm:table-cell">{emp.phone}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={`text-xs ${['Sales Girl', 'Sales Man'].includes(emp.job_role) ? 'bg-red-50 text-red-700' : ''}`}>
                      {emp.job_role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-neutral-600 hidden md:table-cell">{emp.branch_name}</TableCell>
                  <TableCell className="text-xs text-neutral-500 hidden md:table-cell">{emp.joining_date || '-'}</TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] ${emp.emp_status === 'exited' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {emp.emp_status === 'exited' ? 'Exited' : 'Active'}
                    </Badge>
                    {emp.exit_date && <p className="text-[10px] text-neutral-400 mt-0.5">{emp.exit_date}</p>}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {emp.documents?.length > 0 ? (
                      <Badge variant="secondary" className="text-[10px]">
                        <FileText className="h-3 w-3 mr-1" /> {emp.documents.length}
                      </Badge>
                    ) : <span className="text-[10px] text-neutral-400">None</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="text-xs text-red-600 hover:text-red-700 h-7"
                      onClick={() => navigate(`/employees/${emp.id}/profile`)} data-testid={`view-profile-${emp.id}`}>
                      <User className="h-3 w-3 mr-1" /> Profile
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
